import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactCrop, { Crop, PercentCrop, centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RotateCw, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';
import { trackPhotoUpload } from '@/hooks/useFunnelTracking';

// iOS Safari quietly drops canvas content above ~16 MP. Pre-resize source images
// on iOS when they exceed this budget to avoid silent blank exports.
const IOS_MAX_SOURCE_PIXELS = 16 * 1000 * 1000;
const IOS_PRE_RESIZE_LONG_EDGE = 4096;

function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (ua.includes('Macintosh') && 'ontouchend' in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS && isSafari;
}

/**
 * Returns a downscaled HTMLImageElement-like source (canvas) when the original
 * exceeds the iOS pixel budget. Falls back to the original image otherwise.
 */
function maybePreResizeForIOS(
  image: HTMLImageElement
): CanvasImageSource & { width: number; height: number } {
  const pixels = image.naturalWidth * image.naturalHeight;
  if (!isIOSSafari() || pixels <= IOS_MAX_SOURCE_PIXELS) {
    return Object.assign(image, { width: image.naturalWidth, height: image.naturalHeight });
  }

  const longEdge = Math.max(image.naturalWidth, image.naturalHeight);
  const scale = IOS_PRE_RESIZE_LONG_EDGE / longEdge;
  const targetW = Math.round(image.naturalWidth * scale);
  const targetH = Math.round(image.naturalHeight * scale);

  const off = document.createElement('canvas');
  off.width = targetW;
  off.height = targetH;
  const offCtx = off.getContext('2d');
  if (!offCtx) {
    return Object.assign(image, { width: image.naturalWidth, height: image.naturalHeight });
  }
  offCtx.imageSmoothingEnabled = true;
  offCtx.imageSmoothingQuality = 'high';
  offCtx.drawImage(image, 0, 0, targetW, targetH);
  return Object.assign(off, { width: targetW, height: targetH });
}

/**
 * Validates that a canvas actually contains visible (non-transparent) pixels.
 * Samples 9 distributed points and requires at least 3 with alpha > 10.
 * Catches the iOS Safari "blank canvas after toDataURL" failure mode.
 */
function canvasHasVisiblePixels(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  const w = canvas.width;
  const h = canvas.height;
  if (w < 2 || h < 2) return false;

  const points: Array<[number, number]> = [
    [w / 2, h / 2],
    [w * 0.25, h * 0.25],
    [w * 0.75, h * 0.25],
    [w * 0.25, h * 0.75],
    [w * 0.75, h * 0.75],
    [w * 0.5, h * 0.1],
    [w * 0.5, h * 0.9],
    [w * 0.1, h * 0.5],
    [w * 0.9, h * 0.5],
  ];

  let visibleCount = 0;
  try {
    for (const [x, y] of points) {
      const data = ctx.getImageData(Math.floor(x), Math.floor(y), 1, 1).data;
      if (data[3] > 10) visibleCount++;
    }
  } catch {
    // getImageData can throw on tainted canvases — treat as invalid
    return false;
  }
  return visibleCount >= 3;
}

interface PhotoCropModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string | null;
  onCropComplete: (croppedImage: string) => void;
  /** Custom aspect ratio (width/height). Default: 7/9 (passport) */
  aspect?: number;
  /** Output pixel dimensions. If not provided, defaults based on aspect. */
  outputSize?: { width: number; height: number };
}

// Default passport photo aspect ratio (35mm x 45mm = 7:9)
const DEFAULT_ASPECT_RATIO = 7 / 9;
const DEFAULT_OUTPUT_LONG_EDGE = 900;

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
): Crop {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export const PhotoCropModal: React.FC<PhotoCropModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  onCropComplete,
  aspect,
  outputSize,
}) => {
  // Use provided aspect or default
  const aspectRatio = aspect ?? DEFAULT_ASPECT_RATIO;
  
  const [crop, setCrop] = useState<Crop>();
  // Canonical persisted crop in PERCENT — resolution-independent, immune to
  // px↔% confusion in react-image-crop. Pixel coords are derived at export time.
  const [completedPercentCrop, setCompletedPercentCrop] = useState<PercentCrop>();
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [isImageReady, setIsImageReady] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  // Reset all state when modal opens or image source changes
  useEffect(() => {
    if (isOpen && imageSrc) {
      setCrop(undefined);
      setCompletedPercentCrop(undefined);
      setRotation(0);
      setScale(1);
      setIsImageReady(false);
    }
    
    // Defensive viewport reset when modal closes (fixes iOS Safari zoom shift)
    return () => {
      if (!isOpen) return;
      window.scrollTo(0, 0);
      document.body.style.overflow = '';
    };
  }, [isOpen, imageSrc]);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    // Use natural dimensions for stable, render-independent initialization.
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    const initial = centerAspectCrop(w, h, aspectRatio);
    setCrop(initial);
    // Seed completedPercentCrop so Apply works even without user interaction.
    if (initial.unit === '%') {
      setCompletedPercentCrop(initial as PercentCrop);
    }
    setIsImageReady(true);
  }, [aspectRatio]);

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  }, []);

  const getCroppedImage = useCallback(async () => {
    if (!imgRef.current) return;

    const image = imgRef.current;
    const naturalW = image.naturalWidth;
    const naturalH = image.naturalHeight;

    if (!naturalW || !naturalH) {
      console.warn('[PhotoCropModal] Image not yet loaded — abort export', {
        naturalW,
        naturalH,
        complete: image.complete,
      });
      toast.error('Bild wird noch geladen. Bitte einen Moment warten und erneut versuchen.');
      return;
    }

    // Resolve canonical PERCENT crop. ReactCrop's onComplete gives us percent.
    let percentCrop: PercentCrop | undefined = completedPercentCrop;
    if ((!percentCrop || percentCrop.width <= 0 || percentCrop.height <= 0) && crop && crop.unit === '%') {
      percentCrop = crop as PercentCrop;
    }
    if (!percentCrop || percentCrop.width <= 0 || percentCrop.height <= 0) {
      // Fallback to centered aspect crop in percent.
      const fallback = centerAspectCrop(naturalW, naturalH, aspectRatio);
      if (fallback.unit === '%') percentCrop = fallback as PercentCrop;
    }
    if (!percentCrop) return;

    // ───────────────────────────────────────────────────────────────────────────
    // STEP 1: Build a "visual" canvas that mirrors what the modal shows.
    // ReactCrop measures crop percentages against the displayed <img> element.
    // Our <img> has CSS `transform: scale(s) rotate(r)` applied. We replicate
    // that exact transform on an offscreen canvas, sized as the rotation-aware
    // bounding box of (naturalW * 1) x (naturalH * 1) — scale only enlarges
    // visually, it does NOT change ReactCrop's percentage basis (ReactCrop
    // measures the unscaled element's bounding box for percent math).
    //
    // Therefore: percentCrop is relative to the ROTATED-but-not-scaled image.
    // We render a rotated source (no scale), then crop by percent on that.
    // This is exactly what the user sees in the preview rectangle.
    // ───────────────────────────────────────────────────────────────────────────

    // iOS Safari: pre-resize huge sources before any further canvas work.
    const preSource = maybePreResizeForIOS(image);
    const preW = preSource.width;
    const preH = preSource.height;

    const rad = (rotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(rad));
    const absSin = Math.abs(Math.sin(rad));
    const rotW = Math.round(preW * absCos + preH * absSin);
    const rotH = Math.round(preW * absSin + preH * absCos);

    let visualCanvas: HTMLCanvasElement;
    try {
      visualCanvas = document.createElement('canvas');
      visualCanvas.width = rotW;
      visualCanvas.height = rotH;
      const vctx = visualCanvas.getContext('2d');
      if (!vctx) return;
      vctx.imageSmoothingEnabled = true;
      vctx.imageSmoothingQuality = 'high';

      if (rotation !== 0) {
        vctx.translate(rotW / 2, rotH / 2);
        vctx.rotate(rad);
        vctx.drawImage(preSource as CanvasImageSource, -preW / 2, -preH / 2, preW, preH);
      } else {
        vctx.drawImage(preSource as CanvasImageSource, 0, 0, preW, preH);
      }
    } catch (rotErr) {
      console.error('[PhotoCropModal] rotation canvas failed:', rotErr);
      toast.error('Foto konnte nicht verarbeitet werden – bitte erneut hochladen.');
      trackPhotoUpload('fail', { error_message: 'rotate_canvas_failed' });
      return;
    }

    // ───────────────────────────────────────────────────────────────────────────
    // STEP 2: Apply the percent crop on the visual (rotated) canvas.
    // ───────────────────────────────────────────────────────────────────────────
    let sourceX = (percentCrop.x / 100) * rotW;
    let sourceY = (percentCrop.y / 100) * rotH;
    let sourceWidth = (percentCrop.width / 100) * rotW;
    let sourceHeight = (percentCrop.height / 100) * rotH;

    sourceX = Math.max(0, Math.min(sourceX, rotW - 1));
    sourceY = Math.max(0, Math.min(sourceY, rotH - 1));
    sourceWidth = Math.max(1, Math.min(sourceWidth, rotW - sourceX));
    sourceHeight = Math.max(1, Math.min(sourceHeight, rotH - sourceY));

    // Compute output dimensions.
    let outputWidth: number;
    let outputHeight: number;
    if (outputSize) {
      outputWidth = outputSize.width;
      outputHeight = outputSize.height;
    } else {
      if (aspectRatio < 1) {
        outputHeight = DEFAULT_OUTPUT_LONG_EDGE;
        outputWidth = Math.round(outputHeight * aspectRatio);
      } else {
        outputWidth = DEFAULT_OUTPUT_LONG_EDGE;
        outputHeight = Math.round(outputWidth / aspectRatio);
      }
    }

    const canvas = document.createElement('canvas');
    canvas.width = outputWidth;
    canvas.height = outputHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const cropDiag = {
      percentCrop,
      naturalW,
      naturalH,
      preW,
      preH,
      rotW,
      rotH,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      outputWidth,
      outputHeight,
      uiScale: scale,
      rotation,
    };
    console.info('[PhotoCropModal] export', cropDiag);
    try { trackPhotoUpload('attempt', { error_message: 'crop_export_diag', file_size_kb: Math.round(JSON.stringify(cropDiag).length / 1024) }); } catch {}

    try {
      ctx.drawImage(
        visualCanvas,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        outputWidth,
        outputHeight
      );
    } catch (drawErr) {
      console.error('drawImage failed during crop export:', drawErr, cropDiag);
      toast.error('Foto konnte nicht verarbeitet werden – bitte erneut hochladen.');
      trackPhotoUpload('fail', { error_message: 'draw_failed' });
      return;
    }

    if (!canvasHasVisiblePixels(canvas)) {
      console.warn('[PhotoCropModal] Blank canvas detected after drawImage', {
        naturalW: image.naturalWidth,
        naturalH: image.naturalHeight,
        ua: typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown',
      });
      toast.error('Das Foto konnte auf diesem Gerät nicht verarbeitet werden. Bitte ein kleineres Bild (z. B. JPG) verwenden und erneut hochladen.');
      trackPhotoUpload('fail', { error_message: 'blank_canvas' });
      return;
    }

    try {
      const croppedImageUrl = canvas.toDataURL('image/png', 1.0);
      if (!croppedImageUrl || croppedImageUrl.length < 1500) {
        console.warn('[PhotoCropModal] Exported data URL too small', { len: croppedImageUrl?.length });
        toast.error('Das Foto konnte nicht verarbeitet werden. Bitte erneut versuchen.');
        trackPhotoUpload('fail', { error_message: 'tiny_export' });
        return;
      }
      onCropComplete(croppedImageUrl);
    } catch (e) {
      console.error('Failed to export cropped image:', e);
      toast.error('Foto-Export fehlgeschlagen. Bitte erneut versuchen.');
      trackPhotoUpload('fail', { error_message: 'export_exception' });
    }
  }, [crop, completedPercentCrop, rotation, scale, onCropComplete, aspectRatio, outputSize]);

  const handleApply = useCallback(() => {
    getCroppedImage();
  }, [getCroppedImage]);
  
  // Reset crop when aspect ratio changes
  React.useEffect(() => {
    setCrop(undefined);
    setCompletedPercentCrop(undefined);
  }, [aspectRatio]);

  if (!imageSrc) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg">Crop Your Photo</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 flex-1 min-h-0">
          {/* Crop Area */}
          <div className="relative bg-muted rounded-lg overflow-hidden flex items-center justify-center min-h-[200px] sm:min-h-[300px]">
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(_pixel, percent) => setCompletedPercentCrop(percent)}
              aspect={aspectRatio}
              className="max-h-[50vh] sm:max-h-[400px]"
            >
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Crop preview"
                onLoad={onImageLoad}
                style={{
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  maxHeight: '50vh',
                  maxWidth: '100%',
                }}
                className="sm:!max-h-[400px]"
              />
            </ReactCrop>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-3">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 sm:h-9 sm:w-9"
              onClick={handleZoomOut}
              title="Zoom out"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 sm:h-9 sm:w-9"
              onClick={handleZoomIn}
              title="Zoom in"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="h-10 w-10 sm:h-9 sm:w-9"
              onClick={handleRotate}
              title="Rotate 90°"
            >
              <RotateCw className="h-4 w-4" />
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Drag to reposition
          </p>
        </div>

        <DialogFooter className="gap-2 sm:gap-0 mt-2">
          <Button variant="outline" onClick={onClose} className="flex-1 sm:flex-none">
            Cancel
          </Button>
          <Button 
            onClick={handleApply}
            disabled={!isImageReady || (!completedPercentCrop && !crop)}
            className="flex-1 sm:flex-none"
          >
            Apply Crop
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
